import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminLayout from './layouts/AdminLayout';

// Pages
import Dashboard from './pages/dashboard/Dashboard';
import UsersPage from './pages/users/UsersPage';
import LoginPage from './pages/auth/LoginPage';
import WatershedsPage from './pages/watersheds/WatershedsPage';
import WatershedDetailPage from './pages/watersheds/WatershedDetailPage';
import SitesPage from './pages/sites/SitesPage';
import SiteDetailPage from './pages/sites/SiteDetailPage';
import SubmissionsPage from './pages/submissions/SubmissionsPage';
import PointsPage from './pages/points/PointsPage';
import PointDetailPage from './pages/points/PointDetailPage';

/**
 * Protected Route Component
 * 
 * Wrapper component that checks authentication status
 * and redirects to login if not authenticated.
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/**
 * Role-Based Route Component
 * 
 * Wrapper component that checks user role permissions
 * and redirects to dashboard if not authorized.
 */
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * App Routes Component
 * 
 * Main routing logic for the admin panel.
 */
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/point/:pointId" element={<PointDetailPage />} />
      <Route path="/watershed/:watershedId" element={<WatershedDetailPage />} />
      <Route path="/site/:siteId" element={<SiteDetailPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="watersheds" element={<WatershedsPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="submissions" element={<SubmissionsPage />} />
        <Route path="points" element={
          <RoleProtectedRoute allowedRoles={['admin', 'super_admin', 'watershed_manager']}>
            <PointsPage />
          </RoleProtectedRoute>
        } />
        <Route path="users" element={
          <RoleProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <UsersPage />
          </RoleProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
  );
};

/**
 * Main App Component
 * 
 * Root component that sets up providers and routing.
 */
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;